import React, { useState, useEffect } from "react";
import MainLayout from "../components/layout/MainLayout";
import MobileAppConfig from "../features/appconfig/MobileAppConfig";
import appConfigService from "../services/appConfigService";
import { useNotification } from "../context/NotificationContext";

const AppConfig = () => {
  const [appConfig, setAppConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadAppConfig();
  }, [refreshTrigger]);

  const loadAppConfig = async () => {
    try {
      setLoading(true);
      const response = await appConfigService.getMobileAppConfig();
      if (response.success) {
        setAppConfig(response.data);
      }
    } catch (error) {
      console.error("Error loading app config:", error);
      // Set default config if error
      setAppConfig({
        android: { minVersion: "", latestVersion: "", force: false },
        ios: { minVersion: "", latestVersion: "", force: false },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfigUpdate = async (platform, configData) => {
    try {
      const updateData = {
        platform: platform.toLowerCase(),
        minVersion: configData.minVersion,
        latestVersion: configData.latestVersion,
        force: configData.force,
      };

      const response = await appConfigService.updateMobileAppConfig(updateData);
      if (response.success) {
        showSuccess(`${platform} configuration updated successfully!`);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error updating app config:", error);
      showError(`Failed to update ${platform} configuration`);
    }
  };

  return (
    <MainLayout>
      <div className="app-config-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">App Configuration</h1>
            <p className="text-muted mb-0">
              Manage mobile application settings and version control
            </p>
          </div>
          <button
            className="btn btn-outline-primary"
            onClick={loadAppConfig}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>

        {/* Configuration Overview */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="mb-3">
                  <i
                    className="fab fa-android text-success"
                    style={{ fontSize: "3rem" }}
                  ></i>
                </div>
                <h5 className="fw-bold">Android App</h5>
                <p className="text-muted mb-0">
                  Configure Android application versions and update policies
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="mb-3">
                  <i
                    className="fab fa-apple text-dark"
                    style={{ fontSize: "3rem" }}
                  ></i>
                </div>
                <h5 className="fw-bold">iOS App</h5>
                <p className="text-muted mb-0">
                  Configure iOS application versions and update policies
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile App Configuration */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-2">Loading app configuration...</p>
          </div>
        ) : (
          <MobileAppConfig config={appConfig} onUpdate={handleConfigUpdate} />
        )}
      </div>
    </MainLayout>
  );
};

export default AppConfig;
